import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Button, CircularProgress, Typography, makeStyles } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { FlexHorizontal } from '@vestaboard/installables'
import { INSIGHTS_API_BASE_URL } from '../../constants'
import { insightsReportUrl } from './shareUrl'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - 64px - ${theme.spacing(3)}px)`,
    marginBottom: theme.spacing(-3)
  },
  actions: {
    paddingBottom: theme.spacing(2)
  },
  frame: {
    display: 'block',
    flex: 1,
    width: `calc(100% + ${theme.spacing(6)}px)`,
    marginLeft: theme.spacing(-3),
    minHeight: 0,
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    background: '#fff'
  }
}))

export const InternalReportView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const classes = useStyles()
  const { getAccessTokenSilently } = useAuth0()
  const [src, setSrc] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setSrc(null)
    setError(null)
    setCopied(false)
    let cancelled = false
    let objectUrl: string | null = null
    ;(async () => {
      const base = INSIGHTS_API_BASE_URL?.replace(/\/$/, '')
      if (!base) {
        setError('Insights URL is not configured')
        return
      }
      try {
        const token = await getAccessTokenSilently()
        const response = await fetch(`${base}/html-reports/${encodeURIComponent(slug)}`, {
          headers: { 'X-Vestaboard-Token': token }
        })
        if (!response.ok) {
          setError(`Failed to load report (${response.status})`)
          return
        }
        const html = await response.text()
        objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
        if (!cancelled) setSrc(objectUrl)
      } catch {
        if (!cancelled) setError('Failed to load report')
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [getAccessTokenSilently, slug])

  const copyShareLink = async () => {
    const url = insightsReportUrl(slug)
    if (!url) return
    await navigator.clipboard.writeText(url)
    setCopied(true)
  }

  return (
    <div className={classes.root}>
      <div className={classes.actions}>
        <FlexHorizontal spaceBetween>
          <Button component={RouterLink} to='/internal-reports'>
            All reports
          </Button>
          <Button variant='outlined' onClick={copyShareLink}>
            {copied ? 'Copied Insights link' : 'Copy Insights link'}
          </Button>
        </FlexHorizontal>
      </div>
      {error && <Alert severity='error'>{error}</Alert>}
      {!error && !src && <CircularProgress />}
      {src && <iframe title={slug} className={classes.frame} src={src} sandbox='allow-scripts' />}
      {!src && !error && (
        <Typography variant='body2' color='textSecondary'>
          Loading {slug}
        </Typography>
      )}
    </div>
  )
}
